import * as Sentry from '@sentry/react';

// Sentry DSN for Helm web app
const SENTRY_DSN = 'https://0b48d792ceaa60aced65c8999babefc4@o4510751355240448.ingest.us.sentry.io/4510751356813312';

/**
 * Initialize Sentry error tracking
 * Call this once at app startup (in main.jsx)
 */
export function initSentry() {
  const isProduction = window.location.hostname !== 'localhost';

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment based on hostname
    environment: isProduction ? 'production' : 'development',

    // Send default PII data (IP address, etc.)
    sendDefaultPii: true,

    // Integrations
    integrations: [
      // Performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Capture console.error calls
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],

    // Tracing - capture 10% of transactions in production, 100% in dev
    tracesSampleRate: isProduction ? 0.1 : 1.0,

    // Control which URLs get distributed tracing
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/houdashboard-app\.web\.app/,
      /^https:\/\/.*\.cloudfunctions\.net/,
    ],

    // Session Replay
    replaysSessionSampleRate: isProduction ? 0.1 : 0, // 10% in prod, 0% in dev
    replaysOnErrorSampleRate: 1.0, // 100% when errors occur

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Network errors that aren't actionable
      'Failed to fetch',
      'NetworkError',
      'Network request failed',
      'Load failed',
      // Firebase auth errors (expected during normal flow)
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
    ],

    // Enable in all environments (including localhost for testing)
    enabled: true,

    // Before sending, scrub sensitive data
    beforeSend(event) {
      // Remove any auth tokens from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data?.url) {
            // Remove tokens from URLs
            breadcrumb.data.url = breadcrumb.data.url
              .replace(/token=[^&]+/g, 'token=[REDACTED]')
              .replace(/key=[^&]+/g, 'key=[REDACTED]');
          }
          return breadcrumb;
        });
      }
      return event;
    },
  });

  // Expose Sentry globally for console debugging
  window.Sentry = Sentry;

  console.log('[Sentry] Initialized -', isProduction ? 'production' : 'development');
}

/**
 * Set the current user for error context
 * Call this when user logs in
 */
export function setSentryUser(user) {
  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email,
      username: user.displayName || user.email,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add custom context to errors
 */
export function setSentryContext(name, context) {
  Sentry.setContext(name, context);
}

/**
 * Manually capture an exception
 */
export function captureException(error, context = {}) {
  Sentry.captureException(error, { extra: context });
}

/**
 * Manually capture a message
 */
export function captureMessage(message, level = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add a breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Log info message to Sentry
 */
export function logInfo(message, data = {}) {
  Sentry.logger?.info(message, data);
}

// Re-export ErrorBoundary for use in App
export const SentryErrorBoundary = Sentry.ErrorBoundary;
